import { isIOS, request } from '@kibt/krn-utils';
import { api, IResp } from '@/common/api';
import { useRequest } from 'ahooks';
import { IShelfSourceMap } from '@/utils/shelfSource';
import { requestWithCatch } from '@/request';
import { filterEmptyValues, SCENE, SEARCH_SOURCE } from '@/common/const';
import { webLogger } from '@/common/log';
import { isWeb } from '@/common/device-info';
import { Platform } from 'react-native';

export interface PriceDiscountDetail {
    flashSaleDeductAmount: number;
    distributorDeductAmount: number;
    itemCouponDeductAmount: number;
    displayFlashSaleDeductAmount: string;
    displayDistributorDeductAmount: string;
    displayItemCouponDeductAmount: string;
}

export interface Activity {
    activityId: number;
    status: number;
    endTime: number;
    endTimeStamp: number;
    stock: number;
    skuLimitCount: { [key: string]: number };
    skuPurchasableCount: any;
    saleRate: number;
    displayStock: string;
    marketingActivityType: number;
    threshold: number;
    displayThreshold: string;
    discountAmount: number;
    displayDiscountAmount: string;
}
export interface CurrentTier {
    discount: number;
    displayDiscount: string;
    displayShippingFeeAfterDiscount: string;
    displayThreshold: string;
    marketingActivityType: (1 | 4002)[]; // 1-券，4002-商家运费折扣
    shippingFeeAfterDiscount: number;
    threshold: number;
    tier: number;
}

export interface ShippingFeeDiscountTier {
    discount: number;
    displayDiscount: string;
    displayShippingFeeAfterDiscount: string;
    displayThreshold: string;
    marketingActivityType: (1 | 4002)[]; // 1-券，4002-商家运费折扣
    shippingFeeAfterDiscount: number;
    threshold: number;
    tier: number;
}

export interface ShippingFeeInfo {
    currentTier: CurrentTier;
    displayExpressFee: string;
    displayMaxDiscount: string;
    displayMinDiscount: string;
    expressFee: number;
    marketingActivityType: number[];
    maxDiscount: number;
    minDiscount: number;
    shippingFeeDiscountTier: ShippingFeeDiscountTier[];
}

export interface PhotoInfo {
    photoId: number;
    photoCoverUrl: string;
    photoUrl: string;
    deeplink: string;
}

export interface Spec {
    specId: number;
    specName: string;
    specSortNum: number;
    itemSpecValues: ItemSpecValue[];
}

export interface SameItem {
    itemId: number;
    merchantId: number;
    title: string;
    imageUrls: string[];
    templateImageUrls: string[];
    salePrice: {
        amount: number;
        displayAmount: string;
    };
    totalSoldQuantity: {
        amount: number;
        displayAmount: string;
    };
    extra: {
        girdle: any;
        retrievalInfo: {
            expTag: string;
            llsid: string;
            providerSource?: string;
        };
        skuIdList: number[];
    };
    activityInfo: any;
    activityList: Activity[];
    minPurchase: number;
    tags: any;
    intAbTest: IntAbTestType;
}

export interface ItemSpecValue {
    specId: number;
    specValueId: number;
    specValueName: string;
    specValueSortNum: number;
    imageUrl: string;
    templateImageUrl: string;
}

export interface Sku {
    skuId: number;
    itemId: number;
    skuSpecDesc: string;
    merchantId: number;
    skuPrice: number;
    skuMarketPrice: number;
    shippingFee: number;
    imageUrl: string;
    templateImageUrl: string;
    availableQuantity: number;
    createTime: number;
    updateTime: number;
    displaySkuPrice: string;
    displaySkuMarketPrice: string;
    displayShippingFee: string;
    displayAvailableQuantity: string;
    displayPriceOff: string;
    salePrice: number;
    displaySalePrice: string;
    activityPrice: number;
    displayActivityPrice: string;
    discountRate: number;
    skuSpecs: SkuSpec[];
    priceDiscountDetail: PriceDiscountDetail;
}

export interface SkuSpec {
    specId: number;
    specName: string;
    specSortNum: number;
    specValueId: number;
    specValueName: string;
    specValueSortNum: number;
    imageId: string;
    imageUrl: string;
    templateImageUrl: string;
    itemId: number;
    skuId: number;
    createTime: number;
    updateTime: number;
}

export interface SellerMerchantShop {
    shopId: number;
    merchantId: number;
    userStatus: number;
    imgUrl: string;
    smallImgUrl: any;
    shopName: string;
    commodityCount: number;
}

export interface GirdleStyle {
    activityIcon: string;
    bgImg: string;
    countDownBgColor: string;
    countDownColor: string;
    bgImg2: string;
    mainActivityNameImg: string;
    activityColor: string;
    netPriceColor: string;
    jumpUrl: string;
}

export interface NetPrice {
    name: string;
    price: number;
    displayPrice: string;
    type: string;
    countDownMills: number;
    endTimestamp: number;
    stock: number;
    jumpUrl: string;
}
export interface MainActivity {
    name: string;
    price: number;
    displayPrice: string;
    type: string;
    countDownMills: number;
    endTimestamp: number;
    stock: number;
    jumpUrl: string;
}

export interface Girdle {
    show: boolean;
    defaultSkuId: number;
    style: GirdleStyle;
    netPrice: {
        [key: string]: NetPrice;
    };
    mainActivity: MainActivity;
    priceCalculator: {};
    fallbackTips: string;
}

export interface PromotionView {
    itemId: number;
    promotionId: number;
    feeType: number;
    discountMode: number;
    discountType: number;
    maxDiscountAmount: number;
    rule: any[];
    marketingActivityType: number;
}

export interface ExtraInfo {
    selfOperated: boolean;
    salePriceInclude: any[];
    returnCoin: number;
    returnCoinImageUrl: string;
    displayReturnCoin: string;
    // NOT_IN_ACTIVE_TIME_RANGE-无活动氛围，WARM_UP-预热氛围，IN_PROGRESS-活动期氛围
    atmosphereTimeNode: 'NOT_IN_ACTIVE_TIME_RANGE' | 'WARM_UP' | 'IN_PROGRESS';
    benefit: (1 | 2 | 3)[]; // 利益点1-本地发货，2-券包邮，3-商家包邮,
    userEquity?: number[];
}

export interface PageUrl {
    placeOrderJumpUrl: string;
    shopJumpUrl: string;
}

export interface ServiceRule {
    logisticsType: number; // logisticsType 1代表物流发货，2代表不需要发货例如线上券码
    expressFeeType: number; // expressFeeType 0非法，1包邮(废弃)，2统一运费(废弃)，3平台预估运费
    expressFee: number;
    displayExpressFee: string;
    expressFeeAfterDiscount: number;
    displayExpressFeeAfterDiscount: string;
    deliveryDurationPercentile: number;
    deliveryDurationDay: number;
    defaultDisplayMessage: string;
    displayExpressFeeDiscount: string;
    expressFeeDiscount: number;
}

export interface Address {
    addressContent: string;
    postalCode: string;
    provinceName: string;
    cityName: string;
    districtName: string;
    countryCode: string;
    secretKey: SecretKey;
    id: number;
}

export interface SecretKey {
    key: string;
    iv: string;
}

export interface ShopControl {
    hasShop: boolean;
    shopIcon: string;
}

export interface Shop {
    shopId: number;
    merchantId: number;
    userStatus: number;
    imgUrl: string;
    smallImgUrl: string;
    shopName: string;
    commodityCount: number;
    salesPoints?: {
        /** 正品商家："merchant_official"，平台推荐商家："merchant_platformReco" */
        tagCode?: 'merchant_official' | 'merchant_platformReco';
        /** 标签文案，本次打标文案由后端返回 */
        tips?: string;
        /** 标签类型 */
        tagLabel?: number; // 1,2,3,4,5,9999
    }[];
}

export interface RecoItem {
    itemId: number;
    title: string;
    imageUrl: string;
    templateImageUrl: string;
    displayItemPrice: string;
    displayMarketPrice: string;
    itemPrice: number;
    marketPrice: number;
    currency: string;
    activeStatus: number;
    auditStatus: number;
    salePrice: number;
    displaySalePrice: string;
    activityInfo: any;
    activityPrice: number;
    displayActivityPrice: string;
    salePriceInclude: any;
    discountRate: number;
    serviceRule: ServiceRule;
    jumpUrl: string;
    shop: any;
    discountCoupon: any[];
    totalSoldQuantity: number;
    productDetailJumpUrl: string;
    display: boolean;
}

export interface InstallmentVo {
    installment: number;
    paymentPerInstallment: number;
    interestPerInstallment: number;
    displayPaymentPerInstallment: string;
    displayInterestPerInstallment: string;
    skuId: number;
}

export interface SkuInstallment {
    installment: number;
    paymentPerInstallment: number;
    interestPerInstallment: number;
    displayPaymentPerInstallment: string;
    displayInterestPerInstallment: string;
    skuId: number;
}

export interface WarmUpPrice {
    warmUpPrice: number;
    displayWarmUpPrice: string;
}

export interface BigSaleInfoVo {
    saleName: string;
    salePeroid: string;
    endTimeStamp: number;
    endTimeLeft: number;
    itemWarmUpPrice: WarmUpPrice;
    skuWarmUpPrice: {
        [key: string]: WarmUpPrice;
    };
    warmUpPopupFrequency: number;
    warmUpPopupTips: string;
}

export interface Comment {
    commentId: number;
    productScore: number;
    detail: string;
    imageUrl: string[];
    userName: string;
    userHeadUrl: string;
    anonym: boolean;
    skuSpecDesc: string;
    createTime: number;
    defaultComment: boolean;
    userId: number;
}

export interface TagCnt {
    value: string;
    amount: number;
    displayAmount: string;
}

export interface TotalCommentCnt {
    amount: number;
    displayAmount: string;
}

export interface TagInfoAgg {
    priceTag: any[];
    itemTag: any[];
    merchantTag: {
        tagCode?: string;
        tips?: string;
        headUrl?: string;
        title?: string;
        tagLabel?: number;
    }[];
}

export interface CoverPhoto {
    deeplink: string;
    photoCoverUrl: string;
    photoId: number;
    photoUrl: string;
}

export interface DiscountCoupon {
    couponId: number;
    batchId: number;
    couponState: number;
    name: string;
    discountType: number;
    discountFeeType: number;
    maxDeductAmount: number;
    displayMaxDeductAmount: string;
    deductionAmount: number;
    displayDeductionAmount: string;
    discountPercent: number;
    displayDiscountPercent: string;
    threshold: number;
    displayThreshold: string;
    expireTime: number;
    expiredPeriodType: number;
    expiredPeriodValue: number;
    rangeType: number;
    discountRangeType: number;
    claimQuantity: number;
    couponUseCountDownMills: number;
    autoClaimable: boolean;
    currentChannelType: number;
    couponTag: any[];
}

export interface MasterPhotoCategoryRankPageInfo {
    rankProducts: PageRowData[];
    title: string;
    secondTitle: string;
    backImageUrl: string;
    rankingPageUrl: string;
    rankingType: 'CATEGORY_TOP' | 'CATEGORY_HEAT_RISE';
}
export type IntAbTestType = {
    // 楼层行数
    floorLevel: number;
    // 是否展示购买按钮
    gobuyButton: number;
    // 标题行数
    titleLine: number;
};

export interface PageRowData {
    lotteryInfo: any;
    title: string;
    itemId: number;
    merchantId: number;
    saleCountry: string;
    imageUrls: string[];
    itemImageUrl: any;
    templateImageUrl: any;
    itemPrice: number;
    marketPrice: number;
    totalQuantity: number;
    totalSoldQuantity: number;
    shippingFeeInfo: ShippingFeeInfo;
    displayItemPrice: string;
    displayMarketPrice: string;
    salePrice: number;
    displaySalePrice: string;
    activityPrice: number;
    displayActivityPrice: string;
    displayTotalQuantity: string;
    displayTotalSoldQuantity: string;
    specs: Spec[];
    skus: Sku[];
    sellerMerchantShop: SellerMerchantShop;
    discountCoupon: DiscountCoupon[];
    activityInfo: any;
    activityList: Activity[];
    salePriceInclude: any;
    promotionView: PromotionView;
    minPurchase: number;
    extraInfo: ExtraInfo;
    abTest: {
        addCartOnly: boolean; // 仅加购实验
        multipulButtonForSpecHalfScreen: boolean; // 仅加购情况下加购规格弹窗在达到运费券门槛时是否支持「购买」按钮
        plcStyle: 0; // plc样式
        productDetailPictureUgcLoop: 1;
        waitingTimeNoOperation: 0;
        coolingTimePopup: 2;
        goodReviews: boolean; // 全部商品评价页的好评优先展示、差评沉底折叠、个人评价置顶 https://docs.corp.kuaishou.com/d/home/fcAA6jTgd2KExLTK2JtF7ZytG#section=vodka.qqbcjn1912h0
        ratingFloorHeight: boolean; // 商详页缩短评价楼层，只展示一条评价 https://docs.corp.kuaishou.com/d/home/fcAA6jTgd2KExLTK2JtF7ZytG#section=vodka.qqbcjn1912h0
        wordOfMouth: boolean; // 商品主图的优质好评图、tab筛选功能 https://docs.corp.kuaishou.com/d/home/fcAA6jTgd2KExLTK2JtF7ZytG#section=vodka.qqbcjn1912h0
        [key: string]: any;
        intAbTest: IntAbTestType; // 同款低价ui实验 https://docs.corp.kuaishou.com/d/home/fcAABa3vgWFwoE9T0GJeVWY_D
    };
    makeUpCoupon: any;
    shippingFeeView: any;
    pageUrl: {
        [key: string]: string;
    };
    coverPhoto: CoverPhoto;
    categoryIdList: number[];
    description: string;
    templateImageUrls: string[];
    detail: string;
    returnRuleId: number;
    serviceRule: ServiceRule;
    address: Address;
    activeStatus: number;
    itemNewsTickers: any;
    shopControl: ShopControl;
    shop: Shop;
    recoItems: RecoItem[];
    simpleFreeShipping: boolean;
    expireCoupon: any;
    installmentVO: InstallmentVo;
    skuInstallments: SkuInstallment[];
    displayPriceOff: string;
    displayShoppingCart: boolean;
    displaySpuUgc: string;
    bigSaleInfoVO: BigSaleInfoVo;
    retrievalInfo: any;
    comments: Comment[];
    tagCnt: TagCnt[];
    totalCommentCnt: TotalCommentCnt;
    productDetailJumpUrl: any;
    tagInfoAgg: TagInfoAgg;
    tags: any;
    photoInfo: PhotoInfo;
    priceDiscountDetail: PriceDiscountDetail;
    isCache: boolean;
    girdle: Girdle;
    newUser: boolean;
    masterPhotoCategoryRankPageInfo: MasterPhotoCategoryRankPageInfo;
    categoryRankingPageInfo: CategoryRankingPageInfo;
}

export interface CategoryRankingPageInfo {
    rankingPageUrl: string;
    // "NO SHOW"表示的不展示楼层，"CATEGORY_TOP"表示累计销量榜单；"CATEGORY_HEAT_RISE"表示近 30 天销量榜单
    rankingType: 'CATEGORY_HEAT_RISE' | 'NO SHOW' | 'CATEGORY_TOP';
    productRankTip: string;
    logoImageUrl: string;
    backImageUrl: string;
    categoryId: number;
    isTop: boolean;
}

export interface SearchKeywords {
    result: number;
    message: string;
    data: {
        //推荐词
        presetKeywords: Array<{
            type: number; //"类型, 0 未知, 1 字符串, 2 字符串+图片"
            keyword: string;
            imageUrl: string;
        }>;
        //气泡提示词
        promptWord: Record<string, any>;
        userAbResult: Record<string, boolean>;
    };
    //气泡提示词
    host: string;
    port: number;
    timestamp: number;
}
/**
 *  记录用户确认行为(18岁情趣)
 */

export function reportUserFeature(reportType: number) {
    return request
        .post(api('/rest/o/ecom/product/customer/social/reportUserFeature'), {
            type: reportType,
        })
        .then(res => {
            if (res.result === 1) {
                return res;
            } else {
                return Promise.reject(res);
            }
        })
        .catch(e => {
            // 异常后无需处理，log使用
        });
}

/**
 *  查询商品详情数据
 */

export function getDetailData(params: {
    scene: number | string;
    encId: string;
    encryptedParam: any;
    merchantId?: number | string;
    sellerUserId?: number | string;
    extParams?: string;
    photoId?: number | string;
}) {
    return request
        .get(api('/rest/o/ecom/customer/item/info'), {
            params: filterEmptyValues(params, true),
        })
        .then(res => {
            if (res.result === 1) {
                return res;
            } else {
                return Promise.reject(res);
            }
        });
}
/**
 *  查询商品详情数据-简单版，用于加购规格弹窗
 */
export function useGetDetailDataSimple(params: {
    itemId: number | string;
    encryptedParam: any;
    merchantId?: number | string;
    sellerUserId?: number | string;
    scene?: number | string;
    uniqueKey?: string;
}) {
    const { data, runAsync: getDetailDataSimple } = useRequest(
        () =>
            requestWithCatch.get(api('/rest/o/ecom/customer/item/simpleInfo'), {
                params,
            }),
        {
            cacheKey: `simpleInfo-${params.itemId}${params.uniqueKey ? `-${params.uniqueKey}` : ''}`,
            manual: true,
        }
    );
    return { data, getDetailDataSimple };
}

/**
 *  催下单
 */
export function postRemindBuyMsg(params: { itemId: number | string; merchantId?: number | string }) {
    return request.post(api('/rest/o/ecom/customer/cs/item/remind'), {
        ...params,
    });
}

/**
 * 查询AES加密参数
 */
export function getAesKey(params: { appId: string; bizType: string; version: string }) {
    return request.post(api('/rest/o/ecommerce/privacy/encryption/aes/param/query'), params);
}

// 查询运费接口
export function getFreightCharge(params: {
    addressId?: number | null;
    postCode?: string | null;
    itemId?: number;
    province?: string;
    city?: string;
    district?: string;
    scene?: string;
}) {
    // 过滤空值
    function filterEmptyValues(obj) {
        return Object?.keys(obj)?.reduce((acc, key) => {
            if (obj[key] !== null) {
                acc[key] = obj[key];
            }
            return acc;
        }, {});
    }
    const filterParams = filterEmptyValues(params);
    return request.get(api('/rest/o/ecom/customer/item/expressFee'), { params: filterParams }, { toastError: false } as any).then(res => {
        if (res.result === 1) {
            return res;
        } else {
            return Promise.reject(res);
        }
    });
}

export enum FreightChargeStatus {
    Success = 'success',
    Error = 'error',
}

/**
 * 获取商品详情页点击buy时商铺状态
 */
export function getBuyShopStatus(params: {
    itemId: string | number;
    skuId: string | number;
    extParams: string;
    merchantId?: number | string;
    sellerUserId?: number | string;
}) {
    return request.get(api('/rest/o/ecom/customer/item/status'), { params }).then(res => {
        if (res.result === 1) {
            return res;
        } else {
            return Promise.reject(res);
        }
    });
}

/**
 * 领取优惠券
 */
export function claimSelfBuildCoupon(params: { batchId: number | string }) {
    return request.post(api(`/rest/o/ecommerce/marketing/c/customer/coupon/claim?batchId=${params.batchId}`)).then(res => {
        if (res.result === 1) {
            return res;
        } else {
            return Promise.reject(res);
        }
    });
}
/**
 * 1元申样订单 才会调用的领券
 */
export function claimSelfMultiBuildCouponToB(params) {
    return request.post(api('/rest/o/ecom/business/c/distribute/decision/claimCouponAuto'), params).then(res => {
        if (res.result === 1) {
            return res;
        } else {
            return Promise.reject(res);
        }
    });
}

// 商详页查询优惠劵列表
export function getQueryDetailCouponListAPI(params) {
    // return axios.post(api('/rest/o/ecommerce/marketing/c/customer/coupon/item/list'), params).then((response) => response.data);
    return request.post(api('/rest/o/ecommerce/marketing/c/customer/coupon/item/list'), params).then(res => {
        if (res.result === 1) {
            return res;
        } else {
            return Promise.reject(res);
        }
    });
}

export function getMarketingRecommendKeyword(params: { itemId?: number | string; merchantId?: number | string; searchSource: SEARCH_SOURCE }) {
    return request
        .post(api('/rest/o/ecommerce/marketing/c/customer/kwai/shop/product/search/preset/keywords'), {
            ...params,
        } as unknown)
        .then(res => {
            if (res?.result === 1) {
                return res;
            } else {
                return Promise.reject(res);
            }
        });
}

export function postDistributeSelectionAddSelf(params: { itemIds: (number | string)[]; source?: IShelfSourceMap }) {
    return request.post(api('/rest/o/ecom/customer/distribute/shelf/add'), params as any, {}).then(res => {
        if (res.result === 1) {
            return res;
        } else {
            return Promise.reject(res);
        }
    });
}

export function getToBDetailData(params: { itemId: number | string }) {
    return request.get(api('/rest/o/ecom/customer/shelf/seller/item/info'), { params });
}

export function postAddToCart(params: {
    skuId: number;
    skuQuantity: number; // 加购数量
    itemId: string | number;
    merchantId: number;
    // 加购链路埋点
    mediaId?: string;
    mediaSource?: string;
    mediaDetailSource?: string;
    sellerMerchantId?: number;
    sellerUserId?: number;
    recoDetail?: {
        expTag: string;
        llsid: string;
    };
    sceneId?: number;
    /** 商业化广告 */
    clickid?: string;
}) {
    return request.post(api('/rest/o/ecommerce/customer/shopCart/addCartItem'), params);
}

// 购物车凑单页半屏加购接口
export function postAddCartItemWithMakeUp(params: {
    selected: any;
    recommendCartItemList: any;
    skuId: number;
    skuQuantity: number; // 加购数量
    itemId: string | number;
    merchantId: number;
    // 加购链路埋点
    mediaId?: string;
    mediaSource?: string;
    mediaDetailSource?: string;
    sellerMerchantId?: number;
    addressId?: number;
}) {
    return request.post(api('/rest/o/ecommerce/customer/shopCart/addCartItemWithMakeUp'), params, { toastError: false });
}
export interface IShowCase {
    photoFeedViewJson: string;
    photoId: number;
    imageUrl: string[];
    videoInfo?: {
        videoCoverUrl: string;
        videoMainUrl: string;
    }[];
    videoId: number;
    createTime: number;
    content: string;
    userName: string;
    userHeadUrl: string;
}

interface IGalleryRes {
    showcase: IShowCase[];
    cursor: string;
    showType: number[];
    total: number;
}

// type 商详0， 达人1， 买家2
export function getGalleryList(params: { cursor?: string; pageSize: number; type: number; itemId: number }): Promise<IResp<IGalleryRes>> {
    return request.get<any, IResp<IGalleryRes>>(api('/rest/o/ecom/product/customer/comment/showcase'), { params }).then(res => {
        if (res.result === 1) {
            return res;
        } else {
            return Promise.reject(res);
        }
    });
}

// 外投相关 API
export function getCouponPopData(params: { merchantId: number; itemId: number }) {
    return request
        .post(api('/rest/o/ecommerce/marketing/c/customer/outside/coupon/popup'), {
            scene: SCENE.PRODUCT_DETAIL_POPUP_OUTSIDE_WEB,
            ...params,
        })
        .then((res: any) => {
            if (res.result === 1) {
                return res?.data;
            } else {
                return Promise.reject(res);
            }
        });
}
